import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const BulkPaymentModal = ({ isOpen, onClose, onProcessPayments, bills = [] }) => {
  const [selectedBills, setSelectedBills] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [paymentDate, setPaymentDate] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [notes, setNotes] = useState('');
  const [selectAll, setSelectAll] = useState(false);
  
  // Enhanced form fields
  const [checkNumber, setCheckNumber] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [approvedBy, setApprovedBy] = useState('');
  const [paymentPriority, setPaymentPriority] = useState('normal');
  const [autoUpdate, setAutoUpdate] = useState(true);
  const [sendNotification, setSendNotification] = useState(false);
  const [requireApproval, setRequireApproval] = useState(false);

  // Filter bills that can be paid - include draft, pending_approval, and approved
  const payableBills = bills?.filter(bill => 
    bill?.status === 'draft' || 
    bill?.status === 'pending_approval' || 
    bill?.status === 'approved'
  ) || [];

  const paymentMethods = [
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'check', label: 'Check' },
    { value: 'credit_card', label: 'Credit Card' },
    { value: 'debit_card', label: 'Debit Card' },
    { value: 'ach', label: 'ACH Transfer' },
    { value: 'wire', label: 'Wire Transfer' },
    { value: 'cash', label: 'Cash' },
    { value: 'online', label: 'Online Payment' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low Priority' },
    { value: 'normal', label: 'Normal Priority' },
    { value: 'high', label: 'High Priority' },
    { value: 'urgent', label: 'Urgent' }
  ];

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedBills([]);
      setSelectAll(false);
      setPaymentMethod('bank_transfer');
      setPaymentDate(new Date()?.toISOString()?.split('T')?.[0]);
      setNotes('');
      setCheckNumber('');
      setBankAccount('');
      setReferenceNumber('');
      setApprovedBy('');
      setPaymentPriority('normal');
      setAutoUpdate(true);
      setSendNotification(false);
      setRequireApproval(false);
    }
  }, [isOpen]);

  // Handle select all checkbox
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedBills(payableBills?.map(bill => bill?.id) || []);
    } else {
      setSelectedBills([]);
    }
  };

  // Handle individual bill selection
  const handleBillSelection = (billId, checked) => {
    if (checked) {
      setSelectedBills(prev => [...prev, billId]);
    } else {
      setSelectedBills(prev => prev?.filter(id => id !== billId));
      setSelectAll(false);
    }
  };

  // Update select all when individual bills change
  useEffect(() => {
    if (selectedBills?.length === payableBills?.length && payableBills?.length > 0) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedBills, payableBills]);

  // Calculate totals
  const selectedBillsData = payableBills?.filter(bill => selectedBills?.includes(bill?.id)) || [];
  const totalAmount = selectedBillsData?.reduce((sum, bill) => sum + (parseFloat(bill?.total_amount) || 0), 0) || 0;
  const billCount = selectedBills?.length || 0;

  const handleProcessPayments = () => {
    if (selectedBills?.length === 0) {
      alert('Please select at least one bill to process payment.');
      return;
    }

    // Validate required fields based on payment method
    if (paymentMethod === 'check' && !checkNumber?.trim()) {
      alert('Check number is required for check payments.');
      return;
    }

    if ((paymentMethod === 'bank_transfer' || paymentMethod === 'ach' || paymentMethod === 'wire') && !bankAccount?.trim()) {
      alert('Bank account information is required for this payment method.');
      return;
    }

    const paymentData = {
      billIds: selectedBills,
      paymentMethod,
      paymentDate,
      notes,
      totalAmount,
      billCount,
      // Enhanced payment data
      checkNumber: paymentMethod === 'check' ? checkNumber : null,
      bankAccount: ['bank_transfer', 'ach', 'wire']?.includes(paymentMethod) ? bankAccount : null,
      referenceNumber,
      approvedBy,
      paymentPriority,
      autoUpdate,
      sendNotification,
      requireApproval
    };

    onProcessPayments?.(paymentData);
    onClose?.();
  };

  // Helper function to get vendor name with proper fallbacks
  const getVendorName = (bill) => {
    // Handle different vendor data structures
    if (bill?.vendor?.vendor_name) {
      return bill?.vendor?.vendor_name;
    }
    if (bill?.vendor?.name) {
      return bill?.vendor?.name;
    }
    if (bill?.vendor_name) {
      return bill?.vendor_name;
    }
    return 'Unknown Vendor';
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString)?.toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  // Helper function to get status styling
  const getStatusStyle = (status) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'pending_approval':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-blue-100 text-blue-800';
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper function to format status text
  const formatStatus = (status) => {
    if (!status) return 'Unknown';
    return status?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase());
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-6xl max-h-[95vh] overflow-hidden">
        {/* Header with Image */}
        <div className="flex items-center justify-between p-6 border-b border-border bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center space-x-4">
            <div>
              <h2 className="text-2xl font-semibold text-foreground">Bulk Payment</h2>
              <p className="text-muted-foreground mt-1">
                Advanced payment processing with check validation and auto-update features
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          {payableBills?.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="FileX" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No Bills Available</h3>
              <p className="text-muted-foreground mb-4">
                There are no bills available for payment. Bills must be in draft, pending approval, or approved status to be eligible for payment.
              </p>
              <div className="text-sm text-muted-foreground">
                <p>Total bills provided: {bills?.length || 0}</p>
                <p>Bills with payable status: {payableBills?.length || 0}</p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Enhanced Payment Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Payment Method *
                  </label>
                  <Select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e?.target?.value)}
                    options={paymentMethods}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Payment Date *
                  </label>
                  <Input
                    type="date"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e?.target?.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Payment Priority
                  </label>
                  <Select
                    value={paymentPriority}
                    onChange={(e) => setPaymentPriority(e?.target?.value)}
                    options={priorityOptions}
                  />
                </div>
              </div>

              {/* Conditional Payment Method Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {paymentMethod === 'check' && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Check Number *
                    </label>
                    <Input
                      placeholder="Enter check number..."
                      value={checkNumber}
                      onChange={(e) => setCheckNumber(e?.target?.value)}
                    />
                  </div>
                )}

                {(['bank_transfer', 'ach', 'wire']?.includes(paymentMethod)) && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Bank Account *
                    </label>
                    <Input
                      placeholder="Enter bank account details..."
                      value={bankAccount}
                      onChange={(e) => setBankAccount(e?.target?.value)}
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Reference Number
                  </label>
                  <Input
                    placeholder="Optional reference number..."
                    value={referenceNumber}
                    onChange={(e) => setReferenceNumber(e?.target?.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Approved By
                  </label>
                  <Input
                    placeholder="Approver name..."
                    value={approvedBy}
                    onChange={(e) => setApprovedBy(e?.target?.value)}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Payment Notes
                </label>
                <Input
                  placeholder="Add payment notes and instructions..."
                  value={notes}
                  onChange={(e) => setNotes(e?.target?.value)}
                />
              </div>

              {/* Check and Update Options */}
              <div className="bg-muted rounded-lg p-4">
                <h4 className="font-medium text-foreground mb-3 flex items-center">
                  <Icon name="Settings" size={16} className="mr-2" />
                  Check & Update Options
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="auto-update"
                      checked={autoUpdate}
                      onChange={(e) => setAutoUpdate(e?.target?.checked)}
                    />
                    <label htmlFor="auto-update" className="text-sm font-medium text-foreground">
                      Auto-update bill status
                    </label>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="send-notification"
                      checked={sendNotification}
                      onChange={(e) => setSendNotification(e?.target?.checked)}
                    />
                    <label htmlFor="send-notification" className="text-sm font-medium text-foreground">
                      Send vendor notifications
                    </label>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="require-approval"
                      checked={requireApproval}
                      onChange={(e) => setRequireApproval(e?.target?.checked)}
                    />
                    <label htmlFor="require-approval" className="text-sm font-medium text-foreground">
                      Require final approval
                    </label>
                  </div>
                </div>
              </div>

              {/* Bills Selection */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-foreground">Select Bills to Pay</h3>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="select-all"
                      checked={selectAll}
                      onChange={(e) => handleSelectAll(e?.target?.checked)}
                    />
                    <label htmlFor="select-all" className="text-sm font-medium text-foreground">
                      Select All ({payableBills?.length})
                    </label>
                  </div>
                </div>

                <div className="border border-border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-full">
                      <thead className="bg-muted">
                        <tr>
                          <th className="text-left p-3 font-medium text-foreground w-16">Select</th>
                          <th className="text-left p-3 font-medium text-foreground min-w-[120px]">Bill Number</th>
                          <th className="text-left p-3 font-medium text-foreground min-w-[150px]">Vendor</th>
                          <th className="text-left p-3 font-medium text-foreground min-w-[100px]">Amount</th>
                          <th className="text-left p-3 font-medium text-foreground min-w-[100px]">Due Date</th>
                          <th className="text-left p-3 font-medium text-foreground min-w-[120px]">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payableBills?.map((bill) => {
                          const vendorName = getVendorName(bill);
                          const billAmount = parseFloat(bill?.total_amount) || 0;
                          
                          return (
                            <tr key={bill?.id} className="border-t border-border hover:bg-muted/50">
                              <td className="p-3">
                                <Checkbox
                                  id={`bill-${bill?.id}`}
                                  checked={selectedBills?.includes(bill?.id)}
                                  onChange={(e) => handleBillSelection(bill?.id, e?.target?.checked)}
                                />
                              </td>
                              <td className="p-3 font-medium text-foreground">
                                {bill?.bill_number || 'N/A'}
                              </td>
                              <td className="p-3 text-foreground">
                                {vendorName}
                              </td>
                              <td className="p-3 font-semibold text-foreground">
                                ${billAmount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td className="p-3 text-muted-foreground">
                                {formatDate(bill?.due_date)}
                              </td>
                              <td className="p-3">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusStyle(bill?.status)}`}>
                                  {formatStatus(bill?.status)}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Enhanced Payment Summary */}
              {selectedBills?.length > 0 && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-foreground mb-3 flex items-center">
                    <Icon name="Calculator" size={16} className="mr-2" />
                    Payment Summary & Validation
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                    <div className="flex justify-between md:block">
                      <span className="text-muted-foreground">Selected Bills:</span>
                      <span className="font-semibold text-foreground md:block">{billCount}</span>
                    </div>
                    <div className="flex justify-between md:block">
                      <span className="text-muted-foreground">Total Amount:</span>
                      <span className="font-semibold text-foreground md:block">
                        ${totalAmount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="flex justify-between md:block">
                      <span className="text-muted-foreground">Payment Date:</span>
                      <span className="font-semibold text-foreground md:block">
                        {formatDate(paymentDate)}
                      </span>
                    </div>
                    <div className="flex justify-between md:block">
                      <span className="text-muted-foreground">Priority:</span>
                      <span className={`font-semibold md:block ${
                        paymentPriority === 'urgent' ? 'text-red-600' : 
                        paymentPriority === 'high' ? 'text-orange-600' : 'text-foreground'
                      }`}>
                        {priorityOptions?.find(p => p?.value === paymentPriority)?.label}
                      </span>
                    </div>
                  </div>
                  
                  {/* Validation Status */}
                  <div className="mt-4 pt-4 border-t border-blue-200">
                    <div className="flex items-center space-x-2 text-sm">
                      <Icon name="CheckCircle" size={16} className="text-green-600" />
                      <span className="text-green-700 font-medium">
                        Form validation passed - Ready to process payments
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Enhanced Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-gray-50">
          <div className="text-sm text-muted-foreground">
            {selectedBills?.length > 0 ? (
              <div className="flex items-center space-x-4">
                <span>{selectedBills?.length} bill(s) selected for payment</span>
                {autoUpdate && (
                  <span className="flex items-center text-blue-600">
                    <Icon name="RotateCw" size={14} className="mr-1" />
                    Auto-update enabled
                  </span>
                )}
                {requireApproval && (
                  <span className="flex items-center text-orange-600">
                    <Icon name="AlertCircle" size={14} className="mr-1" />
                    Approval required
                  </span>
                )}
              </div>
            ) : payableBills?.length > 0 ? (
              <span>{payableBills?.length} bill(s) available for payment</span>
            ) : (
              <span>No bills available for payment</span>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleProcessPayments}
              disabled={selectedBills?.length === 0}
              className="flex items-center space-x-2"
            >
              <Icon name="CreditCard" size={16} />
              <span>
                {requireApproval ? 'Submit for Approval' : 'Process Payments'} ({selectedBills?.length})
              </span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkPaymentModal;